import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { useQueryClient } from '@tanstack/react-query'
import { LoopApiError, type TotpEnrolment } from '@innerloopco/loopcms-core'
import { Button, Dialog, Field, Input } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { accountKeys } from '../../lib/accountHooks.js'
import { CopyButton } from './CopyButton.js'

export interface TotpEnrolDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Step 1: the QR (rendered client side from the otpauth URL — the secret
 * never goes to a third party) plus the secret for manual entry, then a code
 * to confirm. Step 2: the ten recovery codes, shown this once.
 */
export function TotpEnrolDialog({ open, onClose }: TotpEnrolDialogProps) {
  const queryClient = useQueryClient()
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  useEffect(() => {
    if (!open) {
      setEnrolment(null)
      setQr(null)
      setCode('')
      setError(null)
      setRecoveryCodes(null)
      return
    }
    let cancelled = false
    api
      .enrolTotp()
      .then(async (result) => {
        if (cancelled) return
        setEnrolment(result)
        const url = await QRCode.toDataURL(result.otpauthUrl)
        if (!cancelled) setQr(url)
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(caught instanceof LoopApiError ? caught.message : 'Could not start enrolment.')
      })
    return () => { cancelled = true }
  }, [open])

  const confirm = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError('That code needs six digits.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await api.confirmTotp(code)
      setRecoveryCodes(result.recoveryCodes)
      await queryClient.invalidateQueries({ queryKey: accountKeys.account() })
    } catch (caught) {
      setError(caught instanceof LoopApiError ? caught.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, monospace' } as const

  if (recoveryCodes) {
    // Escape and the backdrop do nothing here: this is the only time the
    // codes are shown, so the step closes through Done alone.
    return (
      <Dialog
        open={open}
        title="Your recovery codes"
        onClose={() => {}}
        actions={<Button variant="primary" onClick={onClose}>Done</Button>}
      >
        <div className="stack" style={{ gap: 'var(--space-3)' }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            Keep these somewhere safe — each works once and this is the only time they are shown.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-1) var(--space-4)', ...mono, fontSize: 13 }}>
            {recoveryCodes.map((item) => <span key={item}>{item}</span>)}
          </div>
          <div className="row">
            <CopyButton text={recoveryCodes.join('\n')} label="Copy all" />
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      title="Turn on two-factor"
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void confirm()} disabled={busy || !enrolment}>
            {busy ? 'Checking…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
          Scan this with your authenticator app, then enter the six-digit code it shows.
        </p>
        {qr ? (
          <img
            alt="QR code for your authenticator app"
            src={qr}
            width={180}
            height={180}
            style={{ borderRadius: 'var(--radius-md)', background: 'white', padding: 8, alignSelf: 'center' }}
          />
        ) : null}
        {enrolment ? (
          <div className="stack" style={{ gap: 6, fontSize: 13 }}>
            <span className="text-muted">Or enter this key in the app:</span>
            <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
              {/* In fours, so it can be read and typed (apps ignore the spaces); it wraps rather than pushing Copy out of the dialog. */}
              <span aria-label="Authenticator key" style={{ ...mono, letterSpacing: '0.08em', flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
                {enrolment.secret.replace(/(.{4})(?=.)/g, '$1 ')}
              </span>
              <span style={{ flex: 'none' }}><CopyButton text={enrolment.secret} /></span>
            </div>
          </div>
        ) : null}
        <Field label="Six-digit code" error={error ?? undefined}>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            style={{ ...mono, fontSize: 17, letterSpacing: '0.22em' }}
          />
        </Field>
      </div>
    </Dialog>
  )
}
